/**
 * Fetches calendar events for an arbitrary date range with real-time subscription.
 * Uses friendly_calendar_events view (JWT-scoped, pre-joined with sport/location/etc).
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { supabase } from '../../lib/supabase';
import { fetchScheduleEvents, type ScheduleEvent } from '../../services/schedule';
import { useCurrentCompany } from '../useCurrentCompany';

export function useScheduleEvents(rangeStart: Date, rangeEnd: Date) {
  const { data: companyId } = useCurrentCompany();
  const queryClient = useQueryClient();

  const startIso = rangeStart.toISOString();
  const endIso = rangeEnd.toISOString();
  const queryKey = ['schedule-events', companyId, startIso, endIso];

  const query = useQuery({
    queryKey,
    queryFn: () => fetchScheduleEvents(startIso, endIso),
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
  });

  // Real-time subscription — refetch when any calendar_event changes
  useEffect(() => {
    if (!companyId) return;

    let debounce: ReturnType<typeof setTimeout>;
    const channel = supabase
      .channel(`schedule-events-${companyId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'calendar_events' }, () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['schedule-events', companyId] });
        }, 800);
      })
      .subscribe();

    return () => {
      clearTimeout(debounce);
      supabase.removeChannel(channel);
    };
  }, [companyId, queryClient]);

  // Group events by date string (YYYY-MM-DD) for calendar rendering
  const eventsByDate = useMemo(() => {
    return (query.data || []).reduce<Record<string, ScheduleEvent[]>>((acc, event) => {
      const date = event.start_time?.split('T')[0];
      if (date) {
        if (!acc[date]) acc[date] = [];
        acc[date].push(event);
      }
      return acc;
    }, {});
  }, [query.data]);

  return {
    events: query.data || [],
    eventsByDate,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
